import { useEffect, useState } from 'react'

/**
 * A versatile hook that maintains a value at regular intervals while enabled.
 *
 * **Counter Mode (default):**
 * When no `calculateValue` is provided, the hook acts as a simple counter that
 * increments by `incrementBy` at each interval. The counter automatically resets
 * to 0 when disabled.
 *
 * **Custom Calculation Mode:**
 * When `calculateValue` is provided, the hook calculates a custom value at each
 * interval, and takes a reading immediately whenever it is enabled or
 * `calculateValue` changes. The value is only updated if it actually changes
 * (uses custom or default comparison function).
 *
 * @param enabled - Whether the interval should be running
 * @param intervalMs - The interval in milliseconds between updates
 * @param options - Optional configuration object
 * @param options.calculateValue - Function that calculates the current value (omit for counter mode)
 * @param options.hasChanged - Function that determines if the value has changed (defaults to !== comparison)
 * @param options.incrementBy - Amount to increment by in counter mode (defaults to intervalMs)
 * @returns The current value (counter or calculated value)
 *
 * @example
 * Counter mode: counts milliseconds while loading
 * const ms = useIntervalCounter(isLoading, 1000, { incrementBy: 1000 })
 *
 * @example
 * Custom calculation mode: updates time since last update
 * const timeSince = useIntervalCounter(
 *   true,
 *   1000,
 *   {
 *     calculateValue: () => getTimeSince(lastUpdate),
 *     hasChanged: (prev, next) => prev.value !== next.value
 *   }
 * )
 */
export function useIntervalCounter<T = number>(
  enabled: boolean,
  intervalMs: number,
  options?: {
    calculateValue?: () => T
    hasChanged?: (prev: T, next: T) => boolean
    incrementBy?: number
  },
): T {
  const { calculateValue, hasChanged, incrementBy = intervalMs } = options || {}

  // For counter mode, we need to initialize with 0
  // For calculation mode, we initialize with the calculated value
  const [value, setValue] = useState<T>(() => {
    if (calculateValue) {
      return calculateValue()
    }
    return 0 as T
  })

  const recalculate = calculateValue
    ? () => {
        const newValue = calculateValue()
        setValue((prev) => {
          const changed = hasChanged ? hasChanged(prev, newValue) : prev !== newValue
          return changed ? newValue : prev
        })
      }
    : undefined

  useEffect(() => {
    if (!enabled) {
      setValue(calculateValue ? calculateValue() : (0 as T))
      return undefined
    }

    // Without this, a changed calculateValue shows a stale reading for a whole interval.
    recalculate?.()

    const intervalId = setInterval(
      () =>
        recalculate ? recalculate() : setValue((prev) => ((prev as number) + incrementBy) as T),
      intervalMs,
    )

    return () => clearInterval(intervalId)
    // recalculate is rebuilt every render; calculateValue is the identity that actually matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, calculateValue, hasChanged, incrementBy])

  return value
}
